import { Component } from '@theme/component';

/**
 * A manually controlled, filterable carousel using native touch scrolling.
 * @typedef {Object} CarouselRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} dots
 * @property {HTMLElement} controls
 * @property {HTMLElement} empty
 * @property {HTMLElement} status
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} [filters]
 * @property {HTMLButtonElement[]} [filter]
 * @property {HTMLElement[]} [cards]
 * @extends {Component<CarouselRefs>}
 */
class ProductCarousel extends Component {
  requiredRefs = ['track', 'dots', 'controls', 'empty', 'status', 'previous', 'next'];
  #abort;
  #observer;
  #frame = 0;
  #settleTimer = 0;
  #positions = [0];
  #visible = [];
  #filters = [];
  #category;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
  }

  #normalize(value = '') {
    return value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  }

  #start() {
    this.#cleanup();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const seen = new Set();
    this.#filters = (this.refs.filter ?? []).filter((button) => {
      const key = this.#normalize(button.dataset.category);
      button.hidden = !key || seen.has(key);
      seen.add(key);
      return !button.hidden;
    });
    if (this.refs.filters) this.refs.filters.hidden = this.#filters.length === 0;
    if (!this.#filters.some((button) => this.#normalize(button.dataset.category) === this.#category)) {
      this.#category = this.#normalize(this.#filters[0]?.dataset.category);
    }
    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.refs.track.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.refs.track);
    this.#applyCategory(this.#category);
  }

  #applyCategory(category, announce = false) {
    this.#category = category;
    clearTimeout(this.#settleTimer);
    for (const button of this.#filters) {
      button.setAttribute('aria-pressed', String(this.#normalize(button.dataset.category) === category));
    }
    this.#visible = (this.refs.cards ?? []).filter((card) => {
      card.hidden = Boolean(category) && this.#normalize(card.dataset.category) !== category;
      return !card.hidden;
    });
    this.refs.empty.hidden = this.#visible.length > 0;
    this.refs.track.classList.toggle('is-single', this.#visible.length === 1);
    this.refs.track.scrollTo({ left: 0, behavior: 'auto' });
    this.#measure();
    if (announce) this.#announce();
  }

  #isRTL() {
    return getComputedStyle(this.refs.track).direction === 'rtl';
  }

  #measure() {
    const track = this.refs.track;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    this.#positions = [0];
    if (max > 2 && this.#visible.length && track.clientWidth > 0) {
      const first = this.#visible[0].getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / (first.width + gap)));
      for (let index = perPage; index < this.#visible.length; index += perPage) {
        const rect = this.#visible[index].getBoundingClientRect();
        const position = Math.min(max, this.#isRTL() ? first.right - rect.right : rect.left - first.left);
        if (position - this.#positions.at(-1) > 2) this.#positions.push(position);
      }
      if (max - this.#positions.at(-1) > 2) this.#positions.push(max);
    }
    const hasOverflow = this.#visible.length > 1 && this.#positions.length > 1;
    // Move focus to the scroller before removing a control that currently has focus.
    if (!hasOverflow && this.refs.controls.contains(document.activeElement)) track.focus({ preventScroll: true });
    this.refs.controls.hidden = !hasOverflow;
    this.#drawDots();
    this.#sync();
  }

  #label(index) {
    return (this.refs.dots.dataset.label ?? '')
      .replace('__index__', String(index + 1))
      .replace('__length__', String(this.#positions.length));
  }

  #drawDots() {
    const dots = this.refs.dots;
    const count = this.#positions.length;
    if (dots.children.length !== count) {
      const focusedIndex = dots.contains(document.activeElement) ? Number(document.activeElement?.dataset.page) : -1;
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'se-products__dot';
        button.dataset.page = String(index);
        button.setAttribute('on:click', '/choosePage');
        button.setAttribute('aria-controls', dots.dataset.controls);
        fragment.append(button);
      }
      dots.replaceChildren(fragment);
      if (focusedIndex >= 0 && !this.refs.controls.hidden) {
        dots.children[Math.min(focusedIndex, count - 1)]?.focus({ preventScroll: true });
      }
    }
    [...dots.children].forEach((button, index) => button.setAttribute('aria-label', this.#label(index)));
  }

  #currentPage() {
    const position = Math.abs(this.refs.track.scrollLeft);
    return this.#positions.reduce((nearest, value, index) =>
      Math.abs(value - position) < Math.abs(this.#positions[nearest] - position) ? index : nearest, 0);
  }

  #sync() {
    const index = this.#currentPage();
    [...this.refs.dots.children].forEach((button, i) => {
      if (i === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    this.refs.previous.disabled = index === 0;
    this.refs.next.disabled = index >= this.#positions.length - 1;
  }

  #announce() {
    this.refs.status.textContent = this.#visible.length
      ? [this.#filters.find((button) => button.getAttribute('aria-pressed') === 'true')?.textContent?.trim(), this.#label(this.#currentPage())].filter(Boolean).join('. ')
      : this.refs.empty.textContent;
  }

  #go(index, animate = true) {
    index = Math.max(0, Math.min(this.#positions.length - 1, index));
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.refs.track.scrollTo({ left: this.#positions[index] * (this.#isRTL() ? -1 : 1), behavior: animate && !reduced ? 'smooth' : 'auto' });
    if (!animate || reduced) { this.#sync(); this.#announce(); }
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#announce(), 180);
  };

  #onKeydown = (event) => {
    if (event.target !== this.refs.track) return;
    let page;
    if (event.key === 'Home') page = 0;
    else if (event.key === 'End') page = this.#positions.length - 1;
    else if (event.key === 'ArrowRight') page = this.#currentPage() + (this.#isRTL() ? -1 : 1);
    else if (event.key === 'ArrowLeft') page = this.#currentPage() + (this.#isRTL() ? 1 : -1);
    else return;
    event.preventDefault();
    this.#go(page);
  };

  #onBlockSelect = (event) => {
    const card = (this.refs.cards ?? []).find((card) => card.dataset.blockId === event.detail?.blockId);
    if (!card) return;
    const category = this.#normalize(card.dataset.category);
    const filter = this.#filters.find((button) => this.#normalize(button.dataset.category) === category);
    this.#applyCategory(filter ? category : '');
    const rect = card.getBoundingClientRect();
    const first = this.#visible[0].getBoundingClientRect();
    const distance = this.#isRTL() ? first.right - rect.right : rect.left - first.left;
    const page = this.#positions.reduce((best, value, index) => Math.abs(value - distance) < Math.abs(this.#positions[best] - distance) ? index : best, 0);
    this.#go(page, false);
  };

  /** Select the clicked merchant-defined category. @param {MouseEvent} event */
  chooseFilter(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    this.#applyCategory(this.#normalize(event.target.dataset.category), true);
  }

  /** Select a dot's scroll position. @param {MouseEvent} event */
  choosePage(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const index = Number(event.target.dataset.page);
    if (Number.isInteger(index)) this.#go(index);
  }

  /** Scroll to the preceding page. */
  previous() { this.#go(this.#currentPage() - 1); }

  /** Scroll to the next page. */
  next() { this.#go(this.#currentPage() + 1); }
}

if (!customElements.get('se-product-carousel')) customElements.define('se-product-carousel', ProductCarousel);
